/**
 * useAutoTranslateString — translate a single dynamic English string into
 * the current i18n language via the existing /api/translate/batch endpoint.
 *
 * Use for DB-stored strings that admins type in (banner titles, etc.) — text
 * the static i18n bundle can't know in advance. Cached in localStorage so each
 * unique string only hits the LLM once per language.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  en: 'English', ar: 'Arabic', zh: 'Chinese', nl: 'Dutch', fr: 'French',
  de: 'German', hi: 'Hindi', id: 'Indonesian', it: 'Italian', ja: 'Japanese',
  ko: 'Korean', ms: 'Malay', pl: 'Polish', pt: 'Portuguese', ru: 'Russian',
  es: 'Spanish', sv: 'Swedish', th: 'Thai', tr: 'Turkish', vi: 'Vietnamese',
};

const CACHE_KEY_PREFIX = 'autotrans_v1';

export const useAutoTranslateString = (text) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [translated, setTranslated] = useState(text);

  useEffect(() => {
    if (!text) { setTranslated(text); return; }
    if (lang === 'en') { setTranslated(text); return; }

// Per-language localStorage cache — NON-SENSITIVE (public LLM translation
// output). Do NOT store auth tokens or PII here.
    const cacheKey = `${CACHE_KEY_PREFIX}_${lang}`;
    let cache = {};
    try { cache = JSON.parse(localStorage.getItem(cacheKey) || '{}'); }
    catch (err) { console.debug('[autoTranslate] cache parse failed:', err?.message); }
    if (cache[text]) { setTranslated(cache[text]); return; }

    let cancelled = false;
    const langName = LANGUAGE_NAMES[lang] || lang;
    axios.post(`${API}/translate/batch`, {
      texts: { _str: text },
      target_language: langName,
    }).then((res) => {
      if (cancelled) return;
      const out = res.data?.translations?._str;
      if (out) {
        setTranslated(out);
        cache[text] = out;
        try { localStorage.setItem(cacheKey, JSON.stringify(cache)); }
        catch (err) { console.debug('[autoTranslate] cache write failed (quota?):', err?.message); }
      }
    }).catch((err) => { console.debug('[autoTranslate] api call failed:', err?.message); });

    return () => { cancelled = true; };
  }, [text, lang]);

  return translated;
};

export default useAutoTranslateString;
